import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { orgTeamService } from '../services/orgTeamService';
import {
  ArrowLeft, UserPlus, Users, Mail, Building2, MapPin, X, Check,
  Loader2, Trash2, PauseCircle, PlayCircle, Pencil, Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ─── Modal de invitar / editar alcance ─────────────────────────────────────────
const InviteModal = ({ sedes, initial, onConfirm, onClose, loading, isEdit }) => {
  const [email, setEmail] = useState(initial?.email || '');
  const [scopeMode, setScopeMode] = useState(
    !initial || initial.scope === 'ALL' ? 'all' : 'specific'
  );
  const [selected, setSelected] = useState(
    Array.isArray(initial?.scope) ? new Set(initial.scope) : new Set()
  );

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const scope = scopeMode === 'all' ? 'ALL' : Array.from(selected);
  const canConfirm = (isEdit || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    && (scopeMode === 'all' || selected.size > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-surface-base border border-line-strong rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-line-subtle flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-kumia-green/20">
              <UserPlus size={18} className="text-kumia-green" />
            </div>
            <h3 className="text-content-primary font-semibold text-sm">
              {isEdit ? 'Editar alcance del gerente' : 'Invitar gerente de cadena'}
            </h3>
          </div>
          <button onClick={onClose} className="text-content-subtle hover:text-content-primary"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {!isEdit && (
            <div>
              <label className="block text-sm text-content-muted mb-2">Email del gerente</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="gerente@ejemplo.com"
                className="w-full px-4 py-2.5 bg-surface-3 border border-line-active rounded-xl text-content-primary placeholder-content-disabled focus:outline-none focus:ring-2 focus:ring-kumia-green/50 text-sm"
                autoFocus
              />
            </div>
          )}
          {isEdit && (
            <p className="text-content-secondary text-sm">
              Editando alcance de <span className="text-content-primary font-medium">{initial.email}</span>.
            </p>
          )}

          <div>
            <label className="block text-sm text-content-muted mb-2">Acceso a sedes</label>
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setScopeMode('all')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${scopeMode === 'all' ? 'bg-kumia-green/15 border-kumia-green/40 text-kumia-green' : 'bg-surface-3 border-line-strong text-content-secondary'}`}
              >
                Todas las sedes
              </button>
              <button
                onClick={() => setScopeMode('specific')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${scopeMode === 'specific' ? 'bg-kumia-green/15 border-kumia-green/40 text-kumia-green' : 'bg-surface-3 border-line-strong text-content-secondary'}`}
              >
                Sedes específicas
              </button>
            </div>

            {scopeMode === 'specific' && (
              <div className="space-y-1.5 max-h-52 overflow-y-auto border border-line-strong rounded-xl p-2">
                {sedes.map((s) => {
                  const checked = selected.has(s.id);
                  return (
                    <button
                      key={s.id}
                      onClick={() => toggle(s.id)}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${checked ? 'bg-kumia-green/10' : 'hover:bg-surface-3'}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-kumia-green border-kumia-green' : 'border-line-active'}`}>
                        {checked && <Check size={12} className="text-black" />}
                      </div>
                      <MapPin size={14} className="text-content-subtle flex-shrink-0" />
                      <span className="text-sm text-content-primary truncate">{s.locationName || s.name}</span>
                    </button>
                  );
                })}
                {sedes.length === 0 && <p className="text-content-subtle text-xs px-2 py-3 text-center">No hay sedes.</p>}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 p-5 pt-0 flex-shrink-0">
          <button onClick={onClose} disabled={loading} className="flex-1 px-4 py-2.5 bg-surface-3 hover:bg-surface-5 text-content-secondary rounded-xl text-sm font-medium disabled:opacity-50">Cancelar</button>
          <button
            onClick={() => onConfirm(email.trim().toLowerCase(), scope)}
            disabled={!canConfirm || loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-kumia-green hover:bg-green-500 text-black disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {isEdit ? 'Guardar' : 'Enviar invitación'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Pantalla principal ────────────────────────────────────────────────────────
const OrgTeam = () => {
  const navigate = useNavigate();
  const { isGroupOwner, orgId, loading: authLoading } = useAuth();

  const [managers, setManagers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { editing?: manager }
  const [actionLoading, setActionLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Guard: solo el dueño de la cadena gestiona gerentes.
  useEffect(() => {
    if (!authLoading && !isGroupOwner) navigate('/chain', { replace: true });
  }, [authLoading, isGroupOwner, navigate]);

  const sedeName = useMemo(() => {
    const map = {};
    sedes.forEach((s) => { map[s.id] = s.locationName || s.name; });
    return map;
  }, [sedes]);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      const [team, locsRes] = await Promise.all([
        orgTeamService.getTeam(),
        httpsCallable(functions, 'restaurants-endpoints-getMyOrgLocations')(),
      ]);
      setManagers(team.staff || []);
      setInvitations(team.invitations || []);
      setSedes(locsRes.data?.locations || []);
    } catch (err) {
      console.error('[OrgTeam] load error:', err);
      toast.error('No se pudo cargar el equipo de la cadena.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { if (isGroupOwner) load(); }, [isGroupOwner, load]);

  const scopeLabel = (scope) => {
    if (scope === 'ALL') return 'Todas las sedes';
    if (Array.isArray(scope)) {
      const names = scope.map((id) => sedeName[id] || id);
      return `${scope.length} sede(s): ${names.join(', ')}`;
    }
    return '—';
  };

  const handleInvite = async (email, scope) => {
    setActionLoading(true);
    try {
      if (modal?.editing) {
        await orgTeamService.manageMember({ action: 'update', uid: modal.editing.uid, scope });
        toast.success('Alcance actualizado.');
      } else {
        await orgTeamService.sendInvitation({ email, scope });
        toast.success(`Invitación enviada a ${email}.`);
      }
      setModal(null);
      await load();
    } catch (err) {
      const msg = err?.message || 'Error en la operación.';
      toast.error(msg.replace('Firebase: ', '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setActionLoading(false);
    }
  };

  const memberAction = async (uid, action) => {
    setBusyId(uid);
    try {
      await orgTeamService.manageMember({ action, uid });
      const labels = { suspend: 'Gerente suspendido.', reactivate: 'Gerente reactivado.', remove: 'Gerente removido.' };
      toast.success(labels[action] || 'Listo.');
      await load();
    } catch (err) {
      const msg = err?.message || 'Error en la operación.';
      toast.error(msg.replace('Firebase: ', '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setBusyId(null);
    }
  };

  const cancelInvite = async (invitationId) => {
    setBusyId(invitationId);
    try {
      await orgTeamService.manageMember({ action: 'cancelInvitation', invitationId });
      toast.success('Invitación cancelada.');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Error al cancelar.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-base via-surface-app to-surface-3">
      {modal && (
        <InviteModal
          sedes={sedes}
          initial={modal.editing ? { email: modal.editing.email, scope: modal.editing.scope } : null}
          isEdit={!!modal.editing}
          onConfirm={handleInvite}
          onClose={() => setModal(null)}
          loading={actionLoading}
        />
      )}

      {/* Header */}
      <header className="border-b border-line-subtle bg-surface-chrome/80 dark:bg-black/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/chain')} className="text-content-subtle hover:text-content-primary p-1.5 rounded-lg hover:bg-surface-3">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Building2 size={18} className="text-kumia-green" />
              <h1 className="text-lg font-bold text-content-primary">Equipo de cadena</h1>
            </div>
          </div>
          <button
            onClick={() => setModal({})}
            className="flex items-center gap-2 bg-kumia-green hover:bg-green-500 text-black px-3 py-2 rounded-lg font-medium text-sm transition-all"
          >
            <UserPlus size={16} />
            <span className="hidden sm:inline">Invitar gerente</span>
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-content-muted mb-6">
          Los gerentes pueden entrar y gestionar las sedes que les asignes, sin ser dueños de la cadena.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-kumia-green" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Gerentes */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} className="text-content-secondary" />
                <h2 className="text-sm font-semibold text-content-primary uppercase tracking-wide">Gerentes ({managers.length})</h2>
              </div>
              {managers.length === 0 ? (
                <div className="text-center py-10 bg-surface-3/30 rounded-xl border border-line-strong/40">
                  <Users size={32} className="mx-auto text-content-subtle mb-2" />
                  <p className="text-content-muted text-sm">Aún no hay gerentes. Invita al primero.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {managers.map((m) => {
                    const suspended = m.status === 'suspended';
                    const busy = busyId === m.uid;
                    return (
                      <div key={m.uid} className="bg-surface-3/40 border border-line-strong/50 rounded-xl p-4 flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-kumia-green/15 flex items-center justify-center flex-shrink-0">
                          <Users size={18} className="text-kumia-green" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-content-primary truncate">{m.name || m.email}</span>
                            {suspended && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border border-yellow-500/30">Suspendido</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-sm text-content-subtle mt-0.5">
                            <Mail size={12} /> <span className="truncate">{m.email}</span>
                          </div>
                          <div className="text-xs text-content-muted mt-1.5">{scopeLabel(m.scope)}</div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button title="Editar alcance" disabled={busy} onClick={() => setModal({ editing: m })} className="p-1.5 rounded-lg text-content-subtle hover:text-content-primary hover:bg-surface-3 disabled:opacity-50">
                            <Pencil size={15} />
                          </button>
                          {suspended ? (
                            <button title="Reactivar" disabled={busy} onClick={() => memberAction(m.uid, 'reactivate')} className="p-1.5 rounded-lg text-green-700 dark:text-green-400 hover:bg-green-500/10 disabled:opacity-50">
                              <PlayCircle size={15} />
                            </button>
                          ) : (
                            <button title="Suspender" disabled={busy} onClick={() => memberAction(m.uid, 'suspend')} className="p-1.5 rounded-lg text-yellow-700 dark:text-yellow-400 hover:bg-yellow-500/10 disabled:opacity-50">
                              <PauseCircle size={15} />
                            </button>
                          )}
                          <button title="Remover" disabled={busy} onClick={() => memberAction(m.uid, 'remove')} className="p-1.5 rounded-lg text-red-700 dark:text-red-400 hover:bg-red-500/10 disabled:opacity-50">
                            {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Invitaciones pendientes */}
            {invitations.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={16} className="text-content-secondary" />
                  <h2 className="text-sm font-semibold text-content-primary uppercase tracking-wide">Invitaciones pendientes ({invitations.length})</h2>
                </div>
                <div className="space-y-3">
                  {invitations.map((inv) => {
                    const busy = busyId === inv.id;
                    return (
                      <div key={inv.id} className="bg-surface-3/30 border border-line-strong/40 rounded-xl p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                          <Mail size={18} className="text-blue-700 dark:text-blue-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-content-primary truncate block">{inv.email}</span>
                          <div className="text-xs text-content-muted mt-0.5">{scopeLabel(inv.scope)}</div>
                        </div>
                        <button disabled={busy} onClick={() => cancelInvite(inv.id)} className="text-sm text-red-700 dark:text-red-400 hover:underline disabled:opacity-50 flex items-center gap-1.5 flex-shrink-0">
                          {busy ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                          Cancelar
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgTeam;
